/**
 * "Today 15:00", "Tomorrow 09:30", "3 Sep 14:00".
 *
 * Used for both directions of time, which is what the previous version got
 * wrong: it was written for a `lastCalledAt`, so it asked "is this at or after
 * midnight today?" and answered yes for every future date. Every scheduled call
 * in the queue read "Today", whatever day it was actually for.
 *
 * Its own module, with `now` injectable, because relative dates are exactly the
 * arithmetic that looks obviously right and is not.
 */

/** Milliseconds in a day, used only to convert a difference already in whole days. */
const DAY = 86_400_000;

export function when(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const days = calendarDaysApart(now, date);

  if (days === 0) return `Today ${time}`;
  if (days === 1) return `Tomorrow ${time}`;
  if (days === -1) return `Yesterday ${time}`;

  // The year only when it is not this one: "3 Sep" is unambiguous for anything
  // within the year and "3 Sep 2027" is noise until it is not.
  const sameYear = date.getFullYear() === now.getFullYear();
  const day = date.toLocaleDateString(
    'en-GB',
    sameYear
      ? { day: 'numeric', month: 'short' }
      : { day: 'numeric', month: 'short', year: 'numeric' },
  );
  return `${day} ${time}`;
}

/**
 * Whole calendar days from `from` to `to`, signed — negative for the past.
 *
 * Both ends are collapsed to local midnight first, so the answer is a difference
 * of dates rather than of instants: 23:30 tonight and 00:30 tomorrow are one day
 * apart, not zero. `Math.round` rather than a division, because a day that
 * crosses a DST boundary is 23 or 25 hours long and flooring would report it as
 * the wrong day.
 */
function calendarDaysApart(from: Date, to: Date): number {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();
  return Math.round((b - a) / DAY);
}
